import React, { useEffect, useState } from "react";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { formatNumber, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DashboardSection from "../../dashboard/components/DashboardSection";
import DashboardCard from "../../dashboard/components/DashboardCard";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { FlameIconSvg } from "../../../svgFiles/FlameIconSvg";
import { WashIconSvg } from "../../../svgFiles/WashIconSvg";
import { RipeningIconSvg } from "../../../svgFiles/RipeningIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { BarChartIconSvg } from "../../../svgFiles/BarChartIconSvg";
import {
  useGetReworkAreaSummaryQuery,
  useGetReworkDashboardOverviewQuery,
  useGetReworkPackingSummaryQuery,
  useGetToastedSummaryQuery,
} from "../rework/reworkApi";
import { useGetHumidSummaryQuery } from "../humid/humidApi";
import { useGetGreenRoomSummaryQuery } from "../greenroom/greenRoomApi";

const MetricBox = ({ label, value, unit }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value">
      {value ?? "0"}
      {unit && <span className="db-metric-unit"> {unit}</span>}
    </div>
  </div>
);

const ReworkDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setIsSingleDay(single);
    setStartDate(start);
    setEndDate(end);
    setDate(start);
    if (single) setLotNumber(makePriorLotNumber(start));
  };

  const {
    data : overviewData ,
    isLoading : overviewLoading ,
    isFetching : overviewFetching ,
    isError : overviewError ,
    refetch : overviewRefetch
  } = useGetReworkDashboardOverviewQuery({
    startDate : startDate ,
    endDate : endDate
  });

  
  useEffect(() => {
    overviewRefetch();
  }, []);

  const rework_area = overviewData?.data.rework_area ?? [];
  const rework_packing = overviewData?.data.rework_packing ?? [];
  const sorting_and_packing = overviewData?.data.sorting_and_packing ?? [];
  const rework_metrics = overviewData?.data.rework_metrics ?? [];
  const humidity_room_overview = overviewData?.data.rework_room_overview?.humidity_room_overview ?? [];
  const toasted_room_overview = overviewData?.data.rework_room_overview?.toasted_room_overview ?? [];
  const green_room_overview = overviewData?.data.rework_room_overview?.green_room_overview ?? [];
  

  // Toasted Items
  const toastedItems = [
    {
      label: _t("total_kg_toasted", "Total KG Toasted"),
      value: formatNumber(toasted_room_overview?.total_kg_toasted ?? 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_hydrating", "Total Carts in Hydrating"),
      value: formatNumber(toasted_room_overview?.total_cart_in_hydrating ?? 0 ),
    },
    {
      label: _t("total_kg_bagged", "Total kg bagged"),
      value: formatNumber(toasted_room_overview?.total_kg_bagged || 0),
      unit: " KG",
    },
  ];

  // Humid Items
  const humidItems = [
    {
      label: _t("total_kg_humid", "Total KG Humid"),
      value: formatNumber(humidity_room_overview?.total_kg_humid || 0),
      unit: " KG",
    },
    {
      label: _t("total_carts_in_oven", "Total Carts in Oven"),
      value: formatNumber(humidity_room_overview?.total_cart_in_oven || 0),
    },
    {
      label: _t("carts_in_cooling_room", "Carts in Cooling Room"),
      value: formatNumber(humidity_room_overview?.total_carts_in_cooling_room || 0),
    },
    {
      label: _t("total_kg_bagged", "Total KG Bagged"),
      value: formatNumber(humidity_room_overview?.total_kg_bagged || 0),
      unit: " KG",
    },
    {
      label: _t("evaporation_prcnt", "Evaporation %"),
      value: formatNumber(humidity_room_overview?.evaporation_prcnt || 0),
    },
  ];

  // Sorting Items
  const sortingItems = [
    {
      label: _t("total_kg_prime", "Total Kg Prime"),
      value: formatNumber(sorting_and_packing?.total_prime_kg || 0),
      unit: " KG",
    },
    {
      label: _t("total_kg_trash", "Total Kg Trash"),
      value: formatNumber(sorting_and_packing?.total_kg_trash || 0),
      unit: " KG",
    },
    {
      label: _t("total_kg_seconds", "Total KG Seconds"),
      value: formatNumber(sorting_and_packing?.total_kg_seconds || 0) ,
      unit: " KG",
    },
  ];

  // Packing Items (for Sorting and Packing section)
  const packingSortingItems = [
    {
      label: _t("total_boxes_packed", "Total Boxes Packed"),
      value: formatNumber(sorting_and_packing?.total_boxes_packed || 0 ,0),
    },
    {
      label: _t("total_prime_cheek_boxes", "Total Prime Cheek Boxes"),
      value: formatNumber(sorting_and_packing?.total_prime_cheek_boxes || 0 ,0),
    },
    {
      label: _t("total_other_cheek_boxes", "Total Other Cheek Boxes"),
      value: formatNumber(sorting_and_packing?.total_cheeks_boxes_other_then_prime || 0 , 0),
    },
    {
      label: _t("total_prime_strips_boxes", "Total Prime Strips Boxes"),
      value: formatNumber(sorting_and_packing?.total_prime_strips_boxes || 0 ,0),
    },
    {
      label: _t("total_other_strips_boxes", "Total Other Strips Boxes"),
      value: formatNumber(sorting_and_packing?.total_strips_boxes_other_then_prime || 0 ,0),
    },
  ];

  // Metrics Items
  const metricsItems = [
    {
      label: _t("prime_cheek_prcnt", "Prime Cheek %"),
      value: formatNumber(rework_metrics?.prime_cheeks_percentage ?? 0),
    },
    {
      label: _t("prime_strips_prcnt", "Prime Strips %"),
      value: formatNumber(rework_metrics?.prime_strips_percentage ?? 0),
    },
    {
      label: _t("total_prime_prcnt", "Total Prime %"),
      value: formatNumber(rework_metrics?.total_prime_percentage ?? 0),
    },
    {
      label: _t("total_trash_prcnt", "Total Trash %"),
      value: formatNumber(rework_metrics?.total_trash_percentage ?? 0),
    },
    {
      label: _t("total_seconds_prcnt", "Total Seconds %"),
      value: formatNumber(rework_metrics?.total_seconds_percentage ?? 0),
    },
  ];

  // Green Room Items
  const greenRoomItems = [
    {
      label: _t("total_green_room_cheeks_kg", "Total Green Room Cheeks Kg"),
      value: formatNumber(green_room_overview?.total_cheeks_in_green_room || 0),
      unit: " KG",
    },
    {
      label: _t("total_green_room_strips_kg", "Total Green Room Strips Kg"),
      value: formatNumber(green_room_overview?.total_strips_in_green_room || 0),
      unit: " KG",
    },
    {
      label: _t("total_kg_green_room", "Total KG in Green Room"),
      value: formatNumber(green_room_overview?.total_kg_in_green_room || 0),
      unit: " KG",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("rework_overview_title", "Rework Overview")}
        </h5>
        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}
          <div className="packing_filter_group">
            <label htmlFor="rework-date-range-filter" className="packing_filter_label">
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter
              setDate={setDate}
              onApplyRange={handleApplyRange}
              triggerId="rework-date-range-filter"
            />
          </div>
        </div>
      </div>

      <div>
        {/* ── Rework Area Section ── */}
        <DashboardSection title={_t("rework_area_overview", "Rework Area Overview")}>

          {/* Rework Area Card */}
          <div className="cmn_card" style={{ overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "12px 16px", background: "#fff8f5", borderBottom: "1px solid #f0e8e0" }}>
              <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 8 }}>
                <CycleIconSvg size={15} color="#ee7225" />
                {_t("rework_area", "Rework Area")}
              </span>
            </div>
            <div style={{ padding: 12 }}>
              <div className="pko-turn2-layout">
                <div className="pko-total-cell pko-box" style={{ background: "#edfaf6", border: "1px solid #c5ede4", borderRadius: 8 }}>
                  <div className="pko-box-label">{_t("total_prime_kg_label", "TOTAL PRIME KG")}</div>
                  <div className="pko-box-value">{formatNumber(rework_area?.total_prime_kg ?? 0)}</div>
                  <div className="pko-box-unit" style={{ color: "#059669" }}>KG</div>
                </div>
                <div className="pko-grid-right">
                  <div className="pko-box" style={{ background: "#edfaf6", border: "1px solid #c5ede4", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("prime_cheeks_kg_label", "PRIME CHEEKS KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_prime_cheeks ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#059669" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "#edfaf6", border: "1px solid #c5ede4", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("prime_strips_kg_label", "PRIME STRIPS KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_prime_strips ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#059669" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "rgb(225, 240, 255)", border: "1px solid #bfdbfe", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("seconds_cheeks_kg_label", "SECONDS CHEEKS KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_seconds_cheeks ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#3b82f6" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "rgb(225, 240, 255)", border: "1px solid #bfdbfe", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("seconds_strips_kg_label", "SECONDS STRIPS KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_seconds_strips ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#3b82f6" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "#fff8f5", border: "1px solid #f0e8e0", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("total_tapita_kg_label", "TOTAL TAPITA KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_tapita_kg ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#ea6c00" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "#fff8f5", border: "1px solid #f0e8e0", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label">{_t("total_pieces_kg_label", "TOTAL PIECES KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_pieces_kg ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#ea6c00" }}>KG</div>
                  </div>
                  <div className="pko-box" style={{ background: "#fef0f0", border: "1px solid #f5c6c6", borderRadius: 8, padding: "12px 10px" }}>
                    <div className="pko-box-label" style={{ color: "#e53935" }}>{_t("rejected_kg_label", "REJECTED KG")}</div>
                    <div className="pko-box-value">{formatNumber(rework_area?.total_rework_trash_kg ?? 0)}</div>
                    <div className="pko-box-unit" style={{ color: "#e53935" }}>KG</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Rework Packing Card */}
          <div className="cmn_card" style={{ overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#fff8f5", borderBottom: "1px solid #f0e8e0" }}>
              <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 8 }}>
                <PackageIconSvg size={15} color="#ee7225" />
                {_t("rework_packing", "Rework Packing")}
              </span>
            </div>
            <div style={{ padding: 12 }}>
              <div className="pko-turn2-layout">
                <div className="pko-total-cell pko-box" style={{ background: "#fff8f5", border: "1px solid #f0e8e0", borderRadius: 8 }}>
                  <div className="pko-box-label">{_t("total_boxes_label", "TOTAL BOXES")}</div>
                  <div className="pko-box-value">{formatNumber(rework_packing?.total_boxes_packed ?? 0 ,0)}</div>
                </div>
                <div className="pko-grid-right">
                  {[
                    { label: _t("prime_cheek_boxes_label", "PRIME CHEEK BOXES"), value: formatNumber(rework_packing?.prime_cheeks_boxes_10lb ?? 0 ,0), unit: "10 LB", bg: "#edfaf6", border: "#c5ede4", unitColor: "#059669" },
                    { label: _t("prime_cheek_boxes_label", "PRIME CHEEK BOXES"), value: formatNumber(rework_packing?.prime_cheeks_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "#edfaf6", border: "#c5ede4", unitColor: "#059669" },
                    { label: _t("prime_strip_boxes_label", "PRIME STRIP BOXES"), value: formatNumber(rework_packing?.prime_strips_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "#edfaf6", border: "#c5ede4", unitColor: "#059669" },
                    { label: _t("seconds_cheek_boxes_label", "SECONDS CHEEK BOXES"), value: formatNumber(rework_packing?.seconds_cheeks_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "rgb(225, 240, 255)", border: "#bfdbfe", unitColor: "#3b82f6" },
                    { label: _t("seconds_strip_boxes_label", "SECONDS STRIP BOXES"), value: formatNumber(rework_packing?.seconds_strips_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "rgb(225, 240, 255)", border: "#bfdbfe", unitColor: "#3b82f6" },
                    { label: _t("tapitas_boxes_label", "TAPITAS BOXES"), value: formatNumber(rework_packing?.total_tapita_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "#fff8f5", border: "#f0e8e0", unitColor: "#ea6c00" },
                    { label: _t("pieces_boxes_label", "PIECES BOXES"), value: formatNumber(rework_packing?.total_pieces_boxes_20lb ?? 0 ,0), unit: "20 LB", bg: "#fff8f5", border: "#f0e8e0", unitColor: "#ea6c00" },
                  ].map((item, i) => (
                    <div key={i} className="pko-box" style={{ background: item.bg, border: `1px solid ${item.border}`, borderRadius: 8, padding: "12px 10px" }}>
                      <div className="pko-box-label">{item.label}</div>
                      <div className="pko-box-value">{item.value}</div>
                      <div className="pko-box-unit" style={{ color: item.unitColor }}>{item.unit}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </DashboardSection>
        
          {/* ── Humidity Room Section ── */}
          <DashboardSection
            title={
              <span className="d-flex align-items-center gap-2">
                {/* <WashIconSvg size={18} color="#60a5fa" /> */}
                {_t("humidity_room_overview", "Rework Room Overview")}
              </span>
            }
          >
            <div className="db-two-col">
              <DashboardCard title={_t("humidity_room_overview", "Humidity Room Overview")} icon={
                <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                  <span className="pf-live-dot" />{_t("live_tracking", "Live Tracking")}
                </span>
              }>
                {humidItems.map((item, i) => (
                  <div key={i} className="db-avg-row">
                    <span>{item.label}</span>
                    <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                  </div>
                ))}
              </DashboardCard>

              <div className="db-two-col" style={{ gridTemplateColumns: "1fr", gap: 13 }}>
                <DashboardCard title={_t("toasted_room_overview", "Toasted Room Overview")} icon={
                  <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                    <span className="pf-live-dot" />{_t("live_tracking", "Live Tracking")}
                  </span>
                }>
                  {toastedItems.map((item, i) => (
                    <div key={i} className="db-avg-row">
                      <span>{item.label}</span>
                      <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                    </div>
                  ))}
                </DashboardCard>

                <DashboardCard title={_t("green_room_overview", "Green Room Overview")} icon={
                  <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                    <span className="pf-live-dot" />{_t("live_tracking", "Live Tracking")}
                  </span>
                }>
                  {greenRoomItems.map((item, i) => (
                    <div key={i} className="db-avg-row">
                      <span>{item.label}</span>
                      <span className="db-avg-val">{item.value ?? "0"}{item.unit && <span className="db-metric-unit">{item.unit}</span>}</span>
                    </div>
                  ))}
                </DashboardCard>
              </div>
            </div>
          </DashboardSection>

        {/* ── Sorting and Packing Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              {/* <CycleIconSvg size={18} color="#60a5fa" /> */}
              {_t("sorting_and_packing", "Sorting and Packing")}
            </span>
          }
        >
          {/* Sorting Subsection */}
          <div style={{ marginBottom: "16px" }}>
            <div className="db-avg-header" style={{ marginBottom: "12px" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <ScaleIconSvg size={15} color="#10b981" />
                {_t("sorting", "Sorting")}
              </span>
            </div>
            <div className="fqm-grid">
              {sortingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box bro-box-green">
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}{item.unit ?? ""}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Packing Subsection */}
          <div>
            <div className="db-avg-header" style={{ marginBottom: "12px" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <PackageIconSvg size={15} color="#f97316" />
                {_t("packing", "Packing")}
              </span>
            </div>
            <div className="fqm-grid">
              {packingSortingItems.map((item, i) => (
                <div key={i} className="pp-brokering-box bro-box-orange">
                  <div className="pp-brokering-box-label">{item.label}</div>
                  <div className="pp-brokering-box-value">
                    {item.value ?? "0"}{item.unit ?? ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>
        {/* ── Metrics Section ── */}
        <DashboardSection title={_t("metrics", "Metrics")}>
          <DashboardCard title={_t("rework_metrics", "Rework Metrics")}>
            <div className="db-evap-row">
              {metricsItems.map((item, i) => (
                <div key={i} className="db-evap-box">
                  <div className="db-evap-label">{_t(item.label, item.label)}</div>
                  <div className="db-evap-value neutral">{formatNumber(item.value ?? 0)}%</div>
                </div>
              ))}
            </div>
          </DashboardCard>
        </DashboardSection>
      </div>
    </div>
  );
};

export default ReworkDashboard;
